import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, Observable, OperatorFunction, throwError } from 'rxjs';
import { AuditTrailTransactionNotesReconciliationItem } from './audit-trail-transaction-notes-reconciliation-item.model';
import { showErrorDialog } from '../../shared/utils';

function toDateOrNull(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function enrichAuditTrailTransactionNotesReconciliationItem(
  item: AuditTrailTransactionNotesReconciliationItem
): AuditTrailTransactionNotesReconciliationItem {
  return {
    ...item,
    tradeDate: toDateOrNull(item.tradeDate),
    valueDate: toDateOrNull(item.valueDate),
    noteGenerated: item.versions && item.versions.length > 0
  };
}

function enrichObservableAuditTrailTransactionNotesReconciliationItems$(): OperatorFunction<
  AuditTrailTransactionNotesReconciliationItem[],
  AuditTrailTransactionNotesReconciliationItem[]
> {
  return (source: Observable<AuditTrailTransactionNotesReconciliationItem[]>) =>
    source.pipe(map((items) => items.map((item) => enrichAuditTrailTransactionNotesReconciliationItem(item))));
}

@Injectable({
  providedIn: 'root'
})
export class AuditTrailTransactionNotesReconciliationService {
  private apiEndpoint = `${environment.apiEndpoint}/api/transaction-confirmations-reconciliation`;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private errorDialog: MatDialog
  ) {}

  public getAuditTrailTransactionNotesReconciliationItems$(
    startDate: Date,
    endDate: Date
  ): Observable<AuditTrailTransactionNotesReconciliationItem[]> {
    return this.http
      .get<AuditTrailTransactionNotesReconciliationItem[]>(`${this.apiEndpoint}`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          showErrorDialog(this.errorDialog, error);
          return throwError(() => error);
        }),
        enrichObservableAuditTrailTransactionNotesReconciliationItems$()
      );
  }

  public loadTransactionConfirmationPreview$(transactionId: number, version: number): Observable<string> {
    return this.http
      .get(`${this.apiEndpoint}/${transactionId}/${version}/preview`, {
        responseType: 'text'
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          showErrorDialog(this.errorDialog, error);
          return throwError(() => error);
        }),
        map((response: string) => `data:application/pdf;base64,${response.replace(/"/g, '')}`)
      );
  }
}
